import {
	find,
	filter,
	groupBy
} from "lodash";
import { FIELD_TEMPLATES } from "constants.js";
import { isValueNullish } from "../utils/valueUtilities";
import { prepareDetailsCountCondition } from "./details-count-condition";
import {
	CONDITION_OPERATORS,
	CONDITION_SCOPES,
	ENGINE_EVALUATIONS,
	ENGINE_TYPES,
	EVALUATION_PREPARERS,
	OPERATOR_ACTIONS,
	AGGREGATION_ACTIONS
} from "../screens/rule-provider/constants.js";

const trueShortCircuitEngines = [
	ENGINE_TYPES.EXISTING
];

const falseShortCircuitEngines = [
	ENGINE_TYPES.CHANGE,
	ENGINE_TYPES.REMOTE
];

export class RuleService {
	constructor($logger, $base64) {
		this.$logger = $logger;
		this.$base64 = $base64;
	}

	async processSingleRule(rule, currentValues, detailValuesById, processingDetailId, evaluationContext) {
		try {
			return await this.processSingleRuleLogic(rule, currentValues, detailValuesById, processingDetailId, evaluationContext);
		}
		catch(error) {
			this.$logger.error(`Unable to evaluate rule ${rule.Id}.`, { rule, error });
			return false;
		}
	}

	groupValuesByDetailId(allValues, forDetailId) {
		const notDeletedValues = allValues.filter(v => v.IsDeleted !== true);
		const groupedVals = groupBy(notDeletedValues, v => v.DetailId);
		const detailVals = filter(groupedVals, (v, key) => key !== "undefined");
		const headerVal = groupedVals[undefined] ?? [];
		
		const detailValuesById = {};

		if(detailVals && detailVals.length > 0) {
			detailVals.forEach((detail) => {
				const comboVal = headerVal.concat(detail);
				const detailId = detail[0].DetailId;

				detailValuesById[detailId] = comboVal;
			});
		}

		if(detailValuesById[forDetailId] === undefined) {
			detailValuesById[forDetailId] = headerVal;
		}

		return {
			detailValuesById,
			headerValue: headerVal,
			detailValues: detailVals
		};
	}

	processSingleRuleLogic(rule, currentValues, detailValuesById, processingDetailId, evaluationContext) {
		const operatorAction = rule.IsNot === true 
			? (...args) => !OPERATOR_ACTIONS[rule.Operator.Code](...args)
			: OPERATOR_ACTIONS[rule.Operator.Code];

		if(rule.Engine?.Code === "DETAILS:COUNT") {
			const result = this.evaluateDetailsCount(rule, detailValuesById);
			return Promise.resolve(operatorAction([result]));
		}

		if(rule.Engine && rule.Conditions) {
			if(ENGINE_TYPES[rule.Engine.Code].isAggregate || ENGINE_TYPES[rule.Engine.Code].isConditionAggregate) {
				const detailIdForProcessing = ENGINE_TYPES[rule.Engine.Code].isConditionAggregate 
					&& !ENGINE_TYPES[rule.Engine.Code].isAggregate
					&& processingDetailId !== undefined
					? processingDetailId 
					: undefined;
				const ac = this.evaluateAllConditions(rule, detailValuesById, detailIdForProcessing, evaluationContext);
				const postInverter = rule.IsNot === true ? !ac : ac;
				return Promise.resolve(postInverter);
			}

			const conditionEvaluations = rule.Conditions.map(condition => {
				return this.evaluateCondition(rule, condition, currentValues, evaluationContext);
			});

			return Promise
				.all(conditionEvaluations)
				.then(
					validations => {
						const result = operatorAction(validations);
						return result;
					});
		}
		else if(!!rule.Aggregation) {
			if(rule.Children === undefined) {
				this.$logger.error("An aggregation operator was specified without child rules", rule);
			}

			const aggregationAction = AGGREGATION_ACTIONS[rule.Aggregation.Code];
			const detailResults = [];

			Object.values(detailValuesById).forEach(comboVal => {
				const detailId = comboVal.find(v => v.DetailId !== undefined)?.DetailId;

				const ruleResult = this.handleChildEvaluations(rule, comboVal, detailValuesById, operatorAction, processingDetailId, evaluationContext)
					.then(result => {
						return { result, detailId };
					});

				detailResults.push(ruleResult);
			});

			return Promise.all(detailResults).then(results => {
				const aggregatedResult = aggregationAction(results.map(r => r.result));
				return aggregatedResult;
			});
		}
		else if(!!rule.Children) {
			return this.handleChildEvaluations(rule, currentValues, detailValuesById, operatorAction, processingDetailId, evaluationContext);
		}

		this.$logger.warn(`${rule.Title} (id: ${rule.Id}) has neither children nor conditions with an engine.`);
		return new Promise(resolve => resolve(false));
	}

	handleChildEvaluations = (rule, values, allDetails, operatorAction, processingDetailId, evaluationContext) => {
		const childrenEvaluations = rule.Children.map(childRule => {
			return this.processSingleRule(childRule, values, allDetails, processingDetailId, evaluationContext);
		});

		return Promise
			.all(childrenEvaluations)
			.then(validations => {
				const result = operatorAction(validations);

				return result;
			});
	}

	evaluateDetailsCount(rule, detailValuesById) {
		const { evaluator, value } = prepareDetailsCountCondition(rule);
		// Web's one details collection is evaluated once at its report parent.
		const count = Object.values(detailValuesById).filter(values => values.some(v =>
			!isValueNullish(v.DetailId) && v.IsDeleted !== true)).length;
		return evaluator([count], { value });
	}

	evaluateCondition(rule, condition, values = [], evaluationContext) {
		if(trueShortCircuitEngines.indexOf(ENGINE_TYPES[rule.Engine.Code]) > -1)
			return true;

		else if(falseShortCircuitEngines.indexOf(ENGINE_TYPES[rule.Engine.Code]) > -1) {
			rule.compareValues = values.filter(value => value.FieldId === condition.Field.Id);
			return false;
		}

		const { Field, CompareToField } = condition;
		const templateField = Field && find(evaluationContext.form.Fields, { Id: Field.Id });
		const Template = templateField?.Template;

		const engineType = ENGINE_TYPES[rule.Engine.Code];

		if(!engineType) {
			throw new Error(`${rule.Engine.Code} engine not found.`);
		}

		if(!Template && !engineType.isMetadataEngine) {
			throw new Error(`Can not find field for Id: ${Field.Id}`);
		}

		if(!!engineType.isMetadataEngine && condition.Value === undefined) {
			throw new Error(`Report state engine is missing a value: ${rule.Id}`);
		}

		const fieldValues = engineType.isMetadataEngine ?
			[condition.Value] :
			filter(values, (v) => v.FieldId === Field.Id || v.FieldId === (CompareToField || {}).Id);

		const engine = ENGINE_EVALUATIONS[engineType.function];

		const evaluator = (condition.Operator) ? engine[CONDITION_OPERATORS[condition.Operator.Code]]
			: engine.evaluate;

		const templateName = engineType.isMetadataEngine ? "Metadata" : FIELD_TEMPLATES[Template];

		const provider = EVALUATION_PREPARERS[templateName];

		if(!provider) {
			throw new Error(
				`Can not find operators for template: ${Template}`
			);
		}

		return provider(
			evaluator,
			condition,
			{
				form: evaluationContext.form,
				report: evaluationContext.report,
				isOnline: window.navigator.onLine
			},
			fieldValues
		);
	}

	evaluateAllConditions(rule, detailValuesById, processingDetailId, evaluationContext) {
		const engineType = ENGINE_TYPES[rule.Engine.Code];
		
		if(trueShortCircuitEngines.indexOf(engineType) > -1)
			return true;

		else if(falseShortCircuitEngines.indexOf(engineType) > -1) {
			return false;
		}

		/// this short circuits state based unique engine rules because the web cannot support them without knowledge of all reports.
		const engineIsUnique = engineType.function === "unique";
		if(engineIsUnique) {
			const hasIsCurrentlyCondition = rule.Conditions.some(condition => condition.Operator?.Code === "ISCURRENTLY");

			if(hasIsCurrentlyCondition) {
				return !rule.IsNot;
			}
		}
		///

		const preparedValuesByDetailId = {};

		if(!engineType) {
			throw new Error(`${rule.Engine.Code} engine not found.`);
		}

		const prepareValuesForDetails = ([key, valuesForDetail]) => {
			rule.Conditions
				// Metadata filters do not contribute field values to aggregate evaluations.
				.filter(condition => condition.Scope === undefined || (
					condition.Scope.Code !== "TYPE" &&
					condition.Scope.Code !== CONDITION_SCOPES.State
				))
				.forEach(condition => {
					const { Field, CompareToField } = condition;
					const { Template } = find(evaluationContext.form.Fields, { Id: Field.Id });
	
					if(!Template) {
						throw new Error(`Can not find field for Id: ${Field.Id}`);
					}
	
					const fieldValues = filter(valuesForDetail, (v) => v.FieldId === Field.Id || v.FieldId === (CompareToField || {}).Id);
	
					const provider = EVALUATION_PREPARERS[FIELD_TEMPLATES[Template]];
	
					if(!provider) {
						throw new Error(
							`Can not find operators for template: ${Template}`
						);
					}

					if(preparedValuesByDetailId[key] === undefined) {
						preparedValuesByDetailId[key] = {};
					}
	
					preparedValuesByDetailId[key][Field.Id] = fieldValues;
				});
		};

		if(processingDetailId !== undefined && !engineType.isAggregate) {
			prepareValuesForDetails([processingDetailId, detailValuesById[processingDetailId]]);
		}
		else {
			Object.entries(detailValuesById).forEach(prepareValuesForDetails);
		}

		const engine = ENGINE_EVALUATIONS[engineType.function];
		const aggregatedResult = engine.evaluate(
			preparedValuesByDetailId, 
			undefined, 
			{ 
				form: evaluationContext.form, 
				rule
			}, 
			{
				$base64: this.$base64,
				$logger: this.$logger
			}
		);

		return aggregatedResult;
	}
}

export default angular
	.module("vesl.services.rule-service", [])
	.service("ruleService", RuleService).name;
